import { getExecutionEnv } from '../utils/env';
import { isWindowUndefined, removeEmptyKeys, shouldTrackUserEvents } from '../utils/helper';
import {
  createPageViewAttributes,
  logEvent,
  logToConsole,
  pushToDataLayer,
} from './helpers';
import {
  type AnalyticsConfig,
  type ClickAttributes,
  EVENTS,
  type LogAttributes,
  type PageViewAttributes,
  type ViewAttributes,
} from './types';

const DEFAULT_CONFIG: Partial<AnalyticsConfig> = {
  isEnabled: true,
  shouldTrack: shouldTrackUserEvents(),
  superAttributes: {
    mode: getExecutionEnv(),
  },
  eventUserAttributes: {},
};

export class Analytics {
  private _app: string;

  private _environment: string;

  private _eventUserAttributes: Record<string, any>;

  private _superAttributes: Record<string, any>;

  private _shouldTrack: boolean;

  private _platform: string;

  private _isLoggedIn: boolean;

  private _isEnabled: boolean;

  constructor(config: AnalyticsConfig) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    this._app = finalConfig.app;
    this._environment = finalConfig.environment;
    this._eventUserAttributes = finalConfig.eventUserAttributes || {};
    this._superAttributes = finalConfig.superAttributes || {};
    this._shouldTrack = finalConfig.shouldTrack || false;
    this._platform = 'web';
    this._isLoggedIn = false;
    this._isEnabled = Boolean(finalConfig.isEnabled);

    this._initializeDataLayer();
    this._enhancePublicMethods();
  }

  setEnabled(isEnabled: boolean): void {
    this._isEnabled = isEnabled;
  }

  setLoggedIn(isLoggedIn: boolean): void {
    this._isLoggedIn = isLoggedIn;
  }

  setApp(app: string): void {
    this._app = app;
  }

  setEventUserAttributes(userAttributes: Record<string, any> = {}): void {
    const cleanedUserAttributes = removeEmptyKeys(userAttributes);
    this._eventUserAttributes = cleanedUserAttributes;
  }

  setSuperAttributes(superAttributes: Record<string, any> = {}): void {
    this._superAttributes = {
      ...this._superAttributes,
      ...superAttributes,
    };
  }

  resetSuperAttributes(): void {
    this._superAttributes = {};
  }

  createClone(config: Partial<AnalyticsConfig> = {}): Analytics {
    const clone = new Analytics({
      app: this._app,
      environment: this._environment,
      shouldTrack: this._shouldTrack,
      superAttributes: this._superAttributes,
      eventUserAttributes: this._eventUserAttributes,
    });
    clone.setLoggedIn(this._isLoggedIn);
    clone.setSuperAttributes(config.superAttributes || {});
    return clone;
  }

  identify(userAnalyticsId: string): void {
    this._trackEvent(EVENTS.identify, { user_id: userAnalyticsId });
    this.setLoggedIn(true);
  }

  setUserAttributes(attributes: Record<string, any>): void {
    if (!this._isLoggedIn) {
      logToConsole('error', 'User not identified yet to set attributes');
      return;
    }

    this._trackEvent(EVENTS.setUserAttributes, attributes);
  }

  setLoggedInUser(userId: string, email: string, fullName: string, _userAttributes: Record<string, any> = {}) {
    const userAttributes = {
      user_id: userId,
      user_email: email,
      full_name: fullName,
      ..._userAttributes,
    };
    this.identify(userId);
    this.setUserAttributes(userAttributes);
    this.setEventUserAttributes(userAttributes);
  }

  pageView(attributes: PageViewAttributes = {}): void {
    const finalAttributes = createPageViewAttributes(attributes);
    this._trackEvent(EVENTS.pageView, finalAttributes);
  }

  click(attributes: ClickAttributes): void {
    this._trackEvent(EVENTS.click, attributes);
  }

  view(attributes: ViewAttributes): void {
    this._trackEvent(EVENTS.view, attributes);
  }

  log(attributes: LogAttributes): void {
    this._trackEvent(EVENTS.log, attributes);
  }

  get app(): string {
    return this._app;
  }

  get environment(): string {
    return this._environment;
  }

  get isEnabled(): boolean {
    return this._isEnabled;
  }

  get platform(): string {
    return this._platform;
  }

  get superAttributes(): Record<string, any> {
    return this._superAttributes;
  }

  private _enhancePublicMethods(): void {
    const methodsToEnhance = [
      'setApp', 'setSuperAttributes', 'resetSuperAttributes', 'createClone',
      'identify', 'setUserAttributes', 'pageview', 'click', 'view', 'log',
    ];
    methodsToEnhance.forEach((methodName) => {
      const method = this[methodName as keyof Analytics] as Function;
      (this[methodName as keyof Analytics] as Function) = this._suppressErrorsFromMethod(method);
    });
  }

  private _suppressErrorsFromMethod(fn: Function): Function {
    return (...args: any[]) => {
      try {
        return fn.apply(this, args);
      } catch (error) {
        logToConsole('error', error);
        return null;
      }
    };
  }

  private _initializeDataLayer(): void {
    if (isWindowUndefined()) return;
    (window as any).dataLayer = (window as any).dataLayer || [];
    (this as any)._dataLayerInitialized = true;
  }

  private _createEventPayload(event: string, _attributes: Record<string, any>): Record<string, any> {
    const { custom: customAttributes, ...attributes } = _attributes;
    return {
      event,
      platform: this.platform,
      environment_id: this.environment,
      app: this.app,
      attributes,
      user_attributes: this._eventUserAttributes,
      custom_attributes: {
        ...this.superAttributes,
        ...customAttributes,
      },
    };
  }

  private _trackEvent(event: string, _attributes: Record<string, any>): void {
    if (!this.isEnabled) return;

    const attributes = removeEmptyKeys(_attributes);
    const eventPayload = this._createEventPayload(event, attributes);

    if (this._shouldTrack) {
      pushToDataLayer(eventPayload);
    } else {
      logEvent(eventPayload);
    }
  }
}

// Export a singleton instance
export const analytics = new Analytics({ app: 'CRM', environment: 'CRM' });
